<template>
    <div class="training-faq">
        <CCard class="mt-3 mb-0" body-wrapper>
            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Classic Style')">
                    Classic Style
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Classic Style')" :duration="400">
                    <p>
                        <strong>Classic Style Description</strong> - This style is widely accepted as a base for the modern rowing
                        stroke. It separates the movement between the legs, body, and arms and utilizes a sequential pattern in
                        that order. Typically, you will see an isolated drive from the legs, followed by a swing from the body
                        near the completion of the legs, and finished by a drawing of the arms after the swing of the body.
                    </p>
                    <p>
                        <strong>Graph Interpretation for Classic Style</strong> - On the Speed & Sequence Graph, you will want to
                        see three distinct curves or domes with minimal overlap. First, legs move to create a rising green curve
                        on the drive, then the blue dome of the back comes on, and finally the pink dome of the arms. The higher
                        the percentage of separation of the Legs/Back domes and the Back/Arms dome, the more your stroke aligns
                        with the qualities of the Classic Style. As a result of the separation, the domes will often be quite
                        rounded due to the dynamic, isolated movement.
                    </p>
                    <img src="../assets/training-faq/classic_style_concept2.webp" alt="classic style concept2 graph">
                </CCollapse>
            </section>

            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Mild Classic Style')">
                    Mild Classic Style
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Mild Classic Style')" :duration="400">
                    <p>
                        <strong>Mild Classic Style Description</strong> - This style is very similar to the Classic Style but
                        allows for slightly more overlap across the Classic sequencing. This means you will still see a
                        sequential utilization of the legs, back, and arms on the drive. However, there will be slightly more
                        overlap within that sequencing. Some coaches or athletes might strive for this to blend the components
                        more, or it could be a style variation based on experience in a certain boat class.
                    </p>
                    <p>
                        <strong>Graph Interpretation</strong> - The Speed & Sequence Graph will show less separated domes and
                        curves that are more proporitionally blended. The percentage of separation of Legs from Back and Back
                        from Arms is not as high as in the Classic Style.
                    </p>
                    <img src="../assets/training-faq/mild_style_boat.webp" alt="mild classic style graph">
                </CCollapse>
            </section>

            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Blended Back-Arms Style')">
                    Blended Back-Arms Style
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Blended Back-Arms Style')" :duration="400">
                    <p>
                        <strong>Blended Back-Arms Style Description</strong> - The legs still drive distinctly to begin the stroke, but then
                        the body and arms are blended to finish the stroke. This means that as the body begins to swing, the
                        arms begin to gather or pull. This stroke is sometimes utilized when the upper body strength of an
                        athlete or crew cannot match the speed generated by their lower body. As a result, blending the back
                        and arms may allow them to continue accelerating the handle speed in a natural manner.
                    </p>
                    <p>
                        <strong>Graph Interpretation</strong> - You will see quite a distinct legs dome on the left side of the Speed &
                        Sequence Graph to begin the drive. As the legs finish, the Back and Arm domes will be closely layered
                        with each other as these two components happen around the same time.
                    </p>
                    <img src="../assets/training-faq/blended_back_arms_rowing_style_boat.webp" alt="Blended Back-Arms Style graph">
                </CCollapse>
            </section>

            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Blended Legs-Back Style')">
                    Blended Legs-Back Style
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Blended Legs-Back Style')" :duration="400">
                    <p>
                        <strong>Blended Legs-Back Style Description</strong> - This stroke has a blending of legs and back off the front of the
                        stroke. After utilizing the legs and back together to begin, the rower will use the arms to comlete the
                        stroke cycle. Some athletes or coaches use this stroke in order to suspend more weight earlier in the
                        stroke. This can sometimes lead to a higher speed of crew movement earlier in the stroke cycle.
                    </p>
                    <p>
                        <strong>Graph Interpretation</strong> - The graph begins with layered domes of the legs and back. Their percentage of
                        separation is low and quite separeted from the arms dome. This means that the legs and back begin the
                        stroke together as both body components create speed together early in the drive.
                    </p>
                    <img src="../assets/training-faq/blended_legs_back_concept2.webp" alt="Blended Legs-Back Style graph">
                </CCollapse>
            </section>

            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Speed & Sequence Graph')">
                    Speed & Sequence Graph
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Speed & Sequence Graph')" :duration="400">
                    <p>
                        <strong>General Information:</strong> This graph is divided into four quadrants by a vertical (Y)
                        white line and a horizontal (X) white line. The first part to the left of the vertical line
                        represents the Drive phase, and the second part to the right is the Recovery phase. The vertical
                        line also represents speed in meters per second. The horizontal line is the duration of the stroke.
                        Please remember that this graph represents and analyzes speed and not force. The curves or domes
                        represent the speed of the movements produced by the Legs (L), Back (B), and Arms (A) in relation
                        to the strokes progress. While the speed of the legs is represented by a green line, the back by
                        a blue line, and the arms by a pink line, the speed of the handle is represented by the dotted
                        line on the graph. On a high level, notice how the speed of the handle builds in positive speed
                        during the drive on the left side of the graph, and then transitions to "negative" speed once you
                        change directions with the handle onto the recovery of the stroke (right side of the graph).
                        The same is generally true of your Legs, Back, Arms regardless of sequencing.
                    </p>
                </CCollapse>
            </section>

            <section class="faq-section mb-2">
                <CButton class="p-0" @click="toggleSection('Duration ratio')">
                    Duration ratio
                    <img src="../assets/img/sort-down.svg" alt="arrow down">
                </CButton>
                <CCollapse class="faq-content mt-2" :show="isExpanded('Duration ratio')" :duration="400">
                    <p>
                        The drive duration ratio is a simple tool showing the ratio of your drive time to the overall stroke
                        cycle. A full stroke that represents 100% includes the drive and the recovery. Your drive duration
                        ratio should vary based on the cadence you are rowing. However, coaches will utilize this tool to as
                        one of several factors to assess the quality of a crew's movement. To show an example, if you are
                        doing steady state on the erg and select "Classic Style II," this would not only check your form for
                        the appropriate classic style telemetry on the erg, but it would also look for a drive phase around
                        30-44% of the stroke cycle. This would also mean that the recovery represents the remaining 70-56% of
                        the stroke cycle. Simply put, this is roughly a 1:2 drive-to-recovery ratio. In comparison, if you
                        select "Classic Style IIII" because you are doing race pace on the erg, then the drive phase is
                        between 40-53% of the entire stroke. As a result, the drive-to-recovery ratio is 1:1.5 to 1:1.18
                        drive-to-recovery ratio. At race pace, some coaches put the ratio closer to 1:1. Therefore, our drive
                        phase at race pace (depending on the boat class and modality) straddles the 50% mark.
                    </p>
                </CCollapse>
            </section>

            <section><strong>Full FAQ:</strong> <a href="/faq">{{baseUrl + '/faq'}}</a></section>
        </CCard>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                expandedSections: [],
            }
        },
        methods: {
            isExpanded(section) {
                return this.expandedSections.includes(section);
            },
            toggleSection(section) {
                if (this.isExpanded(section)) {
                    const index = this.expandedSections.indexOf(section);
                    this.expandedSections.splice(index, 1);
                } else {
                    this.expandedSections.push(section);
                }
            }
        },
        computed: {
            baseUrl() {
                return window.location.protocol + '//' + window.location.host;
            }
        }
    }
</script>

<style scoped lang="scss">
    .faq-content img {
        width: 100%;
    }
</style>
